import React, { Component } from 'react';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import { UTILITY_TYPE_ID_INTERNET } from 'common/utility/constants';

import { SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM_PLAN } from 'api/utilityActionTypes';

import { SCREEN_ID_UTILITY_SETUP_PLACE_ORDER } from 'client/constants/screenIds';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withLocationQuery from 'client/helpers/withLocationQuery';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import branch from 'client/core/branch';
import UtilityPartnerActions from 'client/actions/UtilityPartner';
import UtilityActivationActions from 'client/actions/UtilityActivation';

import SrBox from 'client/components/SrBox';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrUtilitySetupProviderPlanConfirmation from 'client/components/SrUtilitySetup/SrUtilitySetupProviderPlanConfirmation';
import SrText from 'client/components/SrText';
import SrUtilitySetupPage from 'client/components/SrUtilitySetup/SrUtilitySetupPage';

@withTransitionHook
@withScreenId(SCREEN_ID_UTILITY_SETUP_PLACE_ORDER)
@withScrollToTop
@withLocationQuery
@branch(({ match }) => ({
  utilityActivation: ['utilityActivations', 'byShortId', match.params.utilityActivationShortId],
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
  purchaseRequestPartnerLogo: [
    'analytics',
    'experiments',
    'utilityActiveVariants',
    'purchaseRequestPartnerLogo',
  ],
}))
@branch(({ utilityActivation }) => {
  if (utilityActivation) {
    return {
      activationInternetProviders: [
        'utilityProviders',
        'internetPlansByPartnerAddressId',
        utilityActivation.utilityPartnerAddressId,
      ],
    };
  }
  return {};
})
class UtilitySetupPlaceOrderPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Utility Setup | Place Order',
  });

  static fetchData(match, inject) {
    return Promise.all([
      UtilityPartnerActions.getBySlug(inject, {
        slug: match.params.partnerSlug,
      }),
      UtilityActivationActions.getByShortId(inject, {
        utilityActivationShortId: match.params.utilityActivationShortId,
      }),
    ]).then(([, utilityActivation]) => {
      if (utilityActivation) {
        return UtilityActivationActions.getInternetPlans(inject, {
          utilityPartnerAddressId: utilityActivation.utilityPartnerAddressId,
        });
      }
      return Promise.resolve();
    });
  }

  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
    };
  }

  getUtilityActivationItem() {
    const { utilityActivation } = this.props;
    if (!utilityActivation?.items) {
      return null;
    }
    return utilityActivation.items.find((item) => item.type.id === UTILITY_TYPE_ID_INTERNET);
  }

  _onCreatePurchaseRequest = (formValues) => {
    const { dispatch, utilityActivation } = this.props;

    this.setState({
      isLoading: true,
    });

    const utilityActivationItem = this.getUtilityActivationItem();

    const utilityProvider = utilityActivationItem.availableProviders.find(
      (provider) => provider.id === utilityActivationItem.utilityProviderId,
    );

    const payload = {
      utilityActivationItemId: utilityActivationItem.id,
      utilityProviderAnswers: {},
      ...setNullEmptyValues(formValues),
    };

    for (const { fieldId } of utilityProvider?.questions || []) {
      payload.utilityProviderAnswers[fieldId] = payload[fieldId];
      delete payload[fieldId];
    }

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM_PLAN,
      payload: {
        form: global.btoa(JSON.stringify(payload)),
      },
      onSuccess: () => {
        dispatch(UtilityActivationActions.getByShortId, {
          utilityActivationShortId: utilityActivation.shortId,
          forceRefresh: true,
        });
      },
    });
  };

  render() {
    const {
      utilityPartner,
      utilityActivation,
      locationQuery,
      activationInternetProviders,
      purchaseRequestPartnerLogo,
    } = this.props;

    const { isLoading } = this.state;

    if (!utilityPartner || !utilityActivation) {
      return <div className="loading-gif" />;
    }

    const utilityActivationItem = this.getUtilityActivationItem();

    if (
      !utilityActivationItem ||
      !utilityActivationItem.utilityProviderId ||
      !(utilityActivationItem.utilityProviderPlanId || utilityActivationItem.utilityInternetPlanId)
    ) {
      return <div>The utility setup is not ready for order placing</div>;
    }

    if (utilityActivationItem.orderPlacedAt) {
      if (locationQuery.redirectTo) {
        global.location = locationQuery.redirectTo;
      } else {
        return (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Success!"
            underline
            message="Order submitted!"
            maxWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          />
        );
      }
    }

    const utilityProvider = (utilityActivationItem.availableProviders || []).find(
      ({ id }) => id === parseInt(utilityActivationItem.utilityProviderId, 10),
    );

    return (
      <SrUtilitySetupPage
        addressDisplay={utilityActivation.address?.display}
        utilityProviderTheme={utilityProvider?.theme}
        utilityPartner={utilityPartner}
        utilityActivationId={utilityActivation.id}
        logoUrl={utilityProvider?.retailerLogoUrl}
        rightActionComponent={
          <SrBox>
            {purchaseRequestPartnerLogo === 'on' && utilityPartner.logoUrl ? (
              <SrBox
                height="80px"
                width="170px"
                backgroundImage={`url(${utilityPartner.logoUrl})`}
                backgroundSize="contain"
                backgroundPosition="center 20px"
                backgroundRepeat="no-repeat"
                role="img"
                ariaLabel={`${utilityPartner.name} logo`}
              />
            ) : (
              <SrBox>
                <SrText
                  align="right"
                  font="helvetica"
                  display="block"
                  fontSize="10px"
                  lineHeight="12px"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  overflow="hidden"
                >
                  Trusted Referrer: {utilityPartner.name}
                </SrText>
                <SrText
                  align="right"
                  font="helvetica"
                  display="block"
                  fontSize="10px"
                  lineHeight="12px"
                  textOverflow="ellipsis"
                >
                  Processing ID: {utilityActivation.shortId}
                </SrText>
              </SrBox>
            )}
          </SrBox>
        }
      >
        <SrBox padding="20px 0px">
          <SrUtilitySetupProviderPlanConfirmation
            leaseStartDate={utilityActivation.leaseStartDate || null}
            isTransferToPurchase={false}
            utilityActivationItem={utilityActivationItem}
            utilityProviderId={utilityActivationItem.utilityProviderId}
            utilityActivation={utilityActivation}
            utilityProviderPlanId={
              utilityActivationItem.utilityProviderPlanId ??
              utilityActivationItem.utilityInternetPlanId
            }
            utilityProviderPlanVariantId={utilityActivationItem.utilityProviderPlanVariantId}
            utilityProviders={
              activationInternetProviders || utilityActivationItem.availableProviders
            }
            isLoading={isLoading}
            onSubmit={this._onCreatePurchaseRequest}
          />
        </SrBox>
      </SrUtilitySetupPage>
    );
  }
}

UtilitySetupPlaceOrderPage.displayName = 'UtilitySetupPlaceOrderPage';

export default UtilitySetupPlaceOrderPage;
